import {
    Box,
    Button,
    Container,
    Flex,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    Textarea,
    useColorModeValue,
    VStack,
    Grid,
    Icon,
    HStack,
    keyframes,
    useToast,
    IconButton,
    Tooltip,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import { MdEmail, MdPhone, MdLocationOn, MdPerson, MdSend, MdClose, MdChat } from "react-icons/md";
import { BsGithub, BsLinkedin } from "react-icons/bs";

const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-15px); }
`;

const Contact = () => {
    const toast = useToast();
    const formRef = useRef();
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const textColor = useColorModeValue("gray.600", "gray.300");
    const labelBg = useColorModeValue("blue.50", "blue.900");
    const socialBg = useColorModeValue("gray.100", "gray.700");
    const socialHoverBg = useColorModeValue("blue.50", "rgba(59,130,246,0.15)");
    const infoCardBg = useColorModeValue("white", "gray.800");
    const infoCardBorder = useColorModeValue("gray.200", "gray.700");

    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobile: "",
        message: ""
    });
    const [isLoading, setIsLoading] = useState(false);

    // Hard Validation Helpers
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const isMobileValid = /^\d{10}$/.test(formData.mobile);
    const isMessageLongEnough = formData.message.length >= 20;
    const isNameProvided = formData.name.trim().length >= 3;

    const isFormValid = isEmailValid && isMobileValid && isMessageLongEnough && isNameProvided;

    const toggleForm = () => {
        setShowForm((prev) => !prev);
        if (!showForm) {
            setTimeout(() => {
                if (formRef.current) {
                    formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Check message count from localStorage
        const previousMessages = JSON.parse(localStorage.getItem("contactMessages") || "[]");
        const userEmail = formData.email.toLowerCase();
        const userMessageCount = previousMessages.filter(m => m.email.toLowerCase() === userEmail).length;

        if (userMessageCount >= 2) {
            toast({
                title: "Message Limit Reached",
                description: "You have already sent 2 messages. Please wait for a response.",
                status: "warning",
                duration: 5000,
                isClosable: true,
                position: "top"
            });
            return;
        }

        setIsLoading(true);

        // EmailJS Configuration
        const SERVICE_ID = "service_c9cxhvd"; 
        const TEMPLATE_ID = "template_qwnwz1t";
        const PUBLIC_KEY = "ucbsbX_4AqG0gJPFy";

        emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY)
            .then((result) => {
                console.log("Email Successfully Sent:", result.text);
                
                const newMessage = {
                    ...formData,
                    id: Date.now(),
                    timestamp: new Date().toISOString()
                };
                const updatedMessages = [...previousMessages, newMessage];
                localStorage.setItem("contactMessages", JSON.stringify(updatedMessages));

                setIsLoading(false);
                setFormData({ name: "", email: "", mobile: "", message: "" });
                setShowForm(false); // Return to default centered view once sent!

                toast({
                    title: "Message Dispatched!",
                    description: `A professional confirmation email has been dispatched to ${formData.email}. I will personally review your inquiry.`,
                    status: "success",
                    duration: 6000,
                    isClosable: true,
                    position: "top"
                });
            }, (error) => {
                console.error("EmailJS Error:", error.text);
                setIsLoading(false);
                
                // Record locally & reset
                const newMessage = {
                    ...formData,
                    id: Date.now(),
                    timestamp: new Date().toISOString()
                };
                const updatedMessages = [...previousMessages, newMessage];
                localStorage.setItem("contactMessages", JSON.stringify(updatedMessages));

                setFormData({ name: "", email: "", mobile: "", message: "" });
                setShowForm(false); // Return to default centered view once sent!

                toast({
                    title: "Message Recorded",
                    description: "Your message was recorded successfully. Thank you for reaching out!",
                    status: "info",
                    duration: 5000,
                    isClosable: true,
                    position: "top"
                });
            });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === "mobile") {
            const numericValue = value.replace(/\D/g, "").slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numericValue }));
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const socialLinks = [
        { 
            name: "LinkedIn", 
            icon: BsLinkedin, 
            link: "https://www.linkedin.com/in/akshayb-profile", 
        },
        { 
            name: "GitHub", 
            icon: BsGithub, 
            link: "https://github.com/bakshay96", 
        },
    ];

    return (
        <Box 
            id="contact" 
            pt={{ base: 6, md: 8 }} 
            pb={{ base: 14, md: 18 }} 
            position="relative" 
            overflow="hidden"
            bg={useColorModeValue("gray.50", "gray.900")}
            bgImage={useColorModeValue(
                "radial-gradient(at 100% 0%, blue.50 0, transparent 50%)",
                "radial-gradient(at 100% 0%, #1e1b4b 0, transparent 50%)"
            )}
        >
            {/* Background Decorative */}
            <Box
                position="absolute"
                top="40%"
                right="-10%"
                width="400px"
                height="400px"
                bgGradient="radial(blue.400, transparent 70%)"
                borderRadius="full"
                opacity={0.1}
                filter="blur(100px)"
                pointerEvents="none"
                animation={`${float} 12s ease-in-out infinite`}
            />

            <Container maxW="1200px" position="relative" zIndex={1}>
                <VStack spacing={10}>
                    {/* Section Header */}
                    <VStack spacing={3} textAlign="center">
                        <Box
                           as={motion.div}
                           initial={{ opacity: 0, y: 20 }}
                           whileInView={{ opacity: 1, y: 0 }}
                           viewport={{ once: true }}
                        >
                            <Text
                                textTransform="uppercase"
                                fontWeight={700}
                                fontSize="sm"
                                letterSpacing="widest"
                                color="blue.500"
                                bg={labelBg}
                                px={4}
                                py={1}
                                borderRadius="full"
                                mb={4}
                                display="inline-block"
                            >
                                Contact Me
                            </Text>
                            <Text fontSize={["3xl", "4xl", "5xl"]} fontWeight={900}>
                                Let's Build Something <Text as="span" color="blue.500">Amazing</Text>
                            </Text>
                            <Text color={textColor} fontSize="lg" maxW="680px" mx="auto" lineHeight="1.8">
                                Whether you're looking to build an ambitious full-stack product, scale an engineering platform, or collaborate on innovative web & AI solutions — let's connect and transform your vision into reality.
                            </Text>
                        </Box>
                    </VStack>

                    {/* Layout Container */}
                    <Grid 
                        templateColumns={showForm ? { base: "1fr", lg: "1fr 1.3fr" } : "1fr"} 
                        gap={10} 
                        w="full" 
                        maxW={showForm ? "1200px" : "750px"}
                        mx="auto"
                        alignItems="flex-start"
                        transition="all 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
                    >
                        {/* Social Media & Contact Info Container */}
                        <VStack spacing={6} align={showForm ? "stretch" : "center"} w="full">
                            
                            {/* Contact Info Cards */}
                            <Grid 
                                templateColumns={showForm ? "1fr" : { base: "1fr", sm: "1fr 1fr" }} 
                                gap={4} 
                                w="full"
                            >
                                <HStack
                                    as={motion.a}
                                    href="mailto:akshaymali.edu@gmail.com"
                                    target="_blank"
                                    w="full"
                                    p={4}
                                    bg={infoCardBg}
                                    borderRadius="2xl"
                                    borderWidth="1px"
                                    borderColor={infoCardBorder}
                                    spacing={4}
                                    whileHover={{ x: 6, borderColor: "#60A5FA" }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    cursor="pointer"
                                    boxShadow="sm"
                                >
                                    <Flex bg="blue.50" p={3} borderRadius="xl" color="blue.500">
                                        <Icon as={MdEmail} boxSize={5} />
                                    </Flex>
                                    <VStack align="start" spacing={0}>
                                        <Text fontWeight={700} fontSize="xs" color="blue.500">Email</Text>
                                        <Text fontWeight={600} fontSize="sm" color={useColorModeValue("gray.800", "white")}>akshaymali.edu@gmail.com</Text>
                                    </VStack>
                                </HStack>

                                <HStack
                                    as={motion.a}
                                    href="#"
                                    target="_blank"
                                    w="full"
                                    p={4}
                                    bg={infoCardBg}
                                    borderRadius="2xl"
                                    borderWidth="1px"
                                    borderColor={infoCardBorder}
                                    spacing={4}
                                    whileHover={{ x: 6, borderColor: "#60A5FA" }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    cursor="pointer"
                                    boxShadow="sm"
                                >
                                    <Flex bg="blue.50" p={3} borderRadius="xl" color="blue.500">
                                        <Icon as={MdLocationOn} boxSize={5} />
                                    </Flex>
                                    <VStack align="start" spacing={0}>
                                        <Text fontWeight={700} fontSize="xs" color="blue.500">Location</Text>
                                        <Text fontWeight={600} fontSize="sm" color={useColorModeValue("gray.800", "white")}>Buldhana, Maharashtra, India</Text>
                                    </VStack>
                                </HStack>
                            </Grid>

                            {/* Centered Social Media Container */}
                            <Box
                                bg={cardBg}
                                p={{ base: 6, md: 8 }}
                                borderRadius="3xl"
                                borderWidth="1px"
                                borderColor={borderColor}
                                boxShadow="xl"
                                w="full"
                                textAlign={showForm ? "left" : "center"}
                            >
                                <Text fontWeight={800} mb={4} fontSize="xl">
                                    Connect On Social Media
                                </Text>
                                
                                <HStack spacing={4} justify={showForm ? "flex-start" : "center"} wrap="wrap">
                                    {socialLinks.map((social, idx) => (
                                        <Box
                                            key={idx}
                                            as={motion.a}
                                            href={social.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            whileHover={{ y: -4, scale: 1.08 }}
                                            whileTap={{ scale: 0.95 }}
                                            bg={socialBg}
                                            py={3} px={5}
                                            borderRadius="2xl"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                            boxShadow="sm"
                                            cursor="pointer"
                                            color={textColor}
                                            _hover={{ color: "#3B82F6", borderColor: "#60A5FA", bg: socialHoverBg }}
                                            transition="all 0.3s cubic-bezier(0.19, 1, 0.22, 1)"
                                            display="flex"
                                            alignItems="center"
                                            gap={2.5}
                                        >
                                            <Icon as={social.icon} boxSize={5} />
                                            <Text fontSize="sm" fontWeight={700}>{social.name}</Text>
                                        </Box>
                                    ))}

                                    {/* Dedicated Message Icon Button */}
                                    <Tooltip label={showForm ? "Close Message Form" : "Open Message Form"} hasArrow placement="top">
                                        <Box
                                            as={motion.button}
                                            whileHover={{ y: -4, scale: 1.08 }}
                                            whileTap={{ scale: 0.95 }}
                                            bg={showForm ? "blue.500" : socialBg}
                                            color={showForm ? "white" : textColor}
                                            py={3} px={5}
                                            borderRadius="2xl"
                                            borderWidth="1px"
                                            borderColor={showForm ? "blue.500" : borderColor}
                                            boxShadow={showForm ? "0 8px 20px rgba(59,130,246,0.35)" : "sm"}
                                            cursor="pointer"
                                            _hover={{
                                                color: showForm ? "white" : "#3B82F6",
                                                borderColor: "#60A5FA",
                                                bg: showForm ? "blue.600" : socialHoverBg
                                            }}
                                            transition="all 0.3s cubic-bezier(0.19, 1, 0.22, 1)"
                                            display="flex"
                                            alignItems="center"
                                            gap={2.5}
                                            onClick={toggleForm}
                                        >
                                            <Icon as={showForm ? MdClose : MdChat} boxSize={5} />
                                            <Text fontSize="sm" fontWeight={700}>
                                                {showForm ? "Close Form" : "Send Message"}
                                            </Text>
                                        </Box>
                                    </Tooltip>
                                </HStack>
                            </Box>
                        </VStack>

                        {/* Animated Message Form (Only Appears when Message Icon is Clicked) */}
                        <AnimatePresence>
                            {showForm && (
                                <Box
                                    key="contact-form-layer"
                                    as={motion.form}
                                    ref={formRef}
                                    onSubmit={handleSubmit}
                                    initial={{ opacity: 0, x: 40, scale: 0.96 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: 40, scale: 0.96 }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    bg={cardBg}
                                    p={{ base: 6, md: 8 }}
                                    borderRadius="3xl"
                                    borderWidth="2px"
                                    borderColor="blue.500"
                                    boxShadow="2xl"
                                    w="full"
                                >
                                    {/* Form Header */}
                                    <Flex justify="space-between" align="center" mb={6}>
                                        <HStack spacing={2.5}>
                                            <Flex w={9} h={9} borderRadius="full" bg="blue.500" color="white" align="center" justify="center">
                                                <MdChat style={{ width: "20px", height: "20px" }} />
                                            </Flex>
                                            <Text fontSize="xl" fontWeight={900}>Send Direct Message</Text>
                                        </HStack>
                                        <IconButton
                                            aria-label="Close message form"
                                            icon={<MdClose style={{ width: "20px", height: "20px" }} />}
                                            size="sm"
                                            borderRadius="full"
                                            variant="ghost"
                                            onClick={() => setShowForm(false)}
                                        />
                                    </Flex>

                                    <VStack spacing={4}>
                                        <FormControl isRequired>
                                            <FormLabel fontWeight={600} fontSize="sm">Your Name</FormLabel>
                                            <InputGroup>
                                                <InputLeftElement children={<MdPerson color="gray.500" />} />
                                                <Input 
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    type="text" 
                                                    placeholder="Enter your name" 
                                                    borderRadius="xl"
                                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                                />
                                            </InputGroup>
                                            {formData.name && !isNameProvided && (
                                                <Text fontSize="xs" color="red.500" mt={1}>Name must be at least 3 characters.</Text>
                                            )}
                                        </FormControl>

                                        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} w="full">
                                            <FormControl isRequired>
                                                <FormLabel fontWeight={600} fontSize="sm">Email Address</FormLabel>
                                                <InputGroup>
                                                    <InputLeftElement children={<MdEmail color="gray.500" />} />
                                                    <Input 
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        type="email" 
                                                        placeholder="Enter your email" 
                                                        borderRadius="xl" 
                                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                                    />
                                                </InputGroup>
                                                {formData.email && !isEmailValid && (
                                                    <Text fontSize="xs" color="red.500" mt={1}>Please enter a valid email address.</Text>
                                                )}
                                            </FormControl>

                                            <FormControl isRequired>
                                                <FormLabel fontWeight={600} fontSize="sm">Mobile Number</FormLabel>
                                                <InputGroup>
                                                    <InputLeftElement children={<MdPhone color="gray.500" />} />
                                                    <Input 
                                                        name="mobile"
                                                        value={formData.mobile}
                                                        onChange={handleChange}
                                                        type="tel" 
                                                        placeholder="Enter 10-digit number" 
                                                        borderRadius="xl"
                                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                                        required
                                                    />
                                                </InputGroup>
                                                {formData.mobile && !isMobileValid && (
                                                    <Text fontSize="xs" color="red.500" mt={1}>Mobile number must be exactly 10 digits.</Text>
                                                )}
                                            </FormControl>
                                        </Grid>

                                        <FormControl isRequired>
                                            <FormLabel fontWeight={600} fontSize="sm">Message</FormLabel>
                                            <Textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                placeholder="Tell me about your project or inquiry..."
                                                rows={5}
                                                borderRadius="xl"
                                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                            />
                                            {formData.message && !isMessageLongEnough && (
                                                <Text fontSize="xs" color="red.500" mt={1}>
                                                    Inquiry is too short. Please provide at least 20 characters. ({formData.message.length}/20)
                                                </Text>
                                            )}
                                        </FormControl>

                                        <Button
                                            type="submit"
                                            isLoading={isLoading}
                                            isDisabled={!isFormValid}
                                            loadingText="Sending..."
                                            w="full"
                                            bg="blue.500"
                                            color="white"
                                            size="lg"
                                            borderRadius="xl"
                                            leftIcon={<MdSend />}
                                            _hover={{ bg: "#2563EB", transform: "translateY(-2px)", boxShadow: "xl" }}
                                            _disabled={{
                                                bg: "gray.300",
                                                cursor: "not-allowed",
                                                _hover: { bg: "gray.300", transform: "none" }
                                            }}
                                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                        >
                                            Send Message
                                        </Button>
                                    </VStack>
                                </Box>
                            )}
                        </AnimatePresence>
                    </Grid>
                </VStack>
            </Container>
        </Box>
    );
};

export default Contact;